import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Menu, X, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

const LINKS = [
  ["/portal", "Dashboard", "nav-dashboard"],
  ["/portal/new", "New Request", "nav-new-request"],
  ["/portal/appointments", "Appointments", "nav-appointments"],
  ["/portal/quotations", "Quotations", "nav-quotations"],
  ["/portal/history", "History", "nav-history"],
  ["/portal/profile", "Profile", "nav-profile"],
];

export const Brand = ({ sub }) => (
  <span className="flex items-center gap-2">
    <img src="/dj-logo-256.png" alt="DJ Landscapes logo" className="h-12 w-12 rounded-full" data-testid="brand-logo" />
    <span className="leading-tight">
      <span className="block font-heading text-base font-semibold">DJ Landscapes</span>
      {sub && <span className="block text-[11px] uppercase tracking-wider text-muted-foreground">{sub}</span>}
    </span>
  </span>
);

export default function CustomerLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const linkCls = ({ isActive }) =>
    `rounded-md px-3 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${isActive ? "bg-secondary text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`;
  const out = async () => { await logout(); nav("/login"); };
  return (
    <div className="min-h-screen bg-background">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-card focus:p-2">Skip to content</a>
      <header className="sticky top-0 z-30 border-b border-border bg-card/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <NavLink to="/portal" data-testid="brand-link"><Brand sub="Customer portal" /></NavLink>
          <nav className="hidden items-center gap-1 lg:flex" aria-label="Main">
            {LINKS.map(([to, label, tid]) => <NavLink key={to} to={to} end={to === "/portal"} className={linkCls} data-testid={tid}>{label}</NavLink>)}
          </nav>
          <div className="hidden items-center gap-3 lg:flex">
            <span className="text-sm text-muted-foreground" data-testid="nav-user-name">{user?.name}</span>
            <Button variant="outline" size="sm" onClick={out} data-testid="logout-btn"><LogOut className="mr-1 h-4 w-4" />Sign out</Button>
          </div>
          <button className="rounded-md p-2 lg:hidden" onClick={() => setOpen(!open)} aria-label="Toggle menu" aria-expanded={open} data-testid="mobile-menu-btn">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {open && (
          <nav className="flex flex-col gap-1 border-t border-border px-4 py-3 lg:hidden" aria-label="Mobile">
            {LINKS.map(([to, label, tid]) => <NavLink key={to} to={to} end={to === "/portal"} className={linkCls} onClick={() => setOpen(false)} data-testid={`m-${tid}`}>{label}</NavLink>)}
            <Button variant="outline" size="sm" className="mt-2" onClick={out} data-testid="m-logout-btn">Sign out</Button>
          </nav>
        )}
      </header>
      <main id="main" className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8"><Outlet /></main>
      <footer className="mx-auto max-w-7xl px-4 pb-8 text-xs text-muted-foreground sm:px-6 lg:px-8">
        <NavLink to="/privacy" className="underline" data-testid="footer-privacy-link">Privacy information</NavLink>
      </footer>
    </div>
  );
}
